import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ScoringResult } from '../utils/scoringEngine';
import { mergeTrackResults } from '../utils/scoringV3';
import { calculateDevelopmentScore } from '../utils/developmentScore';
import {
    buildPersonalRoadmap,
    pickTodayFocus,
    loadCompletedTasks,
    saveCompletedTasks,
    clearRoadmapState,
    RoadmapPhase,
    TodayFocus,
    taskLeadPreview,
} from '../utils/roadmap';
import { loadContent, clearContentCache, UnifiedContent } from '../utils/contentLoader';
import { loadQuizPages } from '../utils/loadQuizPages';
import { useLang } from '../i18n/LangContext';
import { langToBcp47 } from '../i18n/detectLanguage';
import { useT } from '../i18n/useT';
import trackEvent from '../utils/trackEvent';
import { DIMENSION_ORDER, DIMENSION_COLORS, DimensionId, dimensionLevel, amplitudeBand } from '../utils/dimensionMeta';
import { computeXP, formatXP, ivIdFromTaskId, InterventionContribution } from '../utils/xp';

import {
    TopBar,
    HeroOrb,
    FocusCard,
    DimensionCard,
    PatternsList,
    PendulumGauge,
    TaskList,
    RetakeSection,
    DrillDrawer,
    DetailHero,
    DetailSection,
    DetailMeasureList,
    DetailInsightCard,
    scoreColor,
    colors,
    fonts,
} from './dashboard/editorial';
import { MonthSummary, TaskItem } from './dashboard/editorial/TaskList';
import { FocusContribution } from './dashboard/editorial/FocusCard';

import '../assets/scss/dashboard-editorial.scss';

type DrillKind = 'dimension' | 'pattern' | 'pendulum';

interface DrillState {
    kind: DrillKind;
    id: string;
}

/**
 * Where a "take this direction" click goes: a namespace separate from the
 * funnel quiz, so an already-paid user topping up a direction does not pollute
 * the acquisition funnel's GA paths. That flow skips routing, the direction
 * picker, email and checkout, and returns here when it finishes.
 */
function extendedQuizPath(trackId: string): string {
    return `/quiz-extended/doyourstep-quiz/${trackId}`;
}

function pendulumZoneColor(amplitudePct: number): string {
    if (amplitudePct >= 75) return '#EF4444';
    if (amplitudePct >= 55) return '#F59E0B';
    return '#22C55E';
}

function nameFromEmail(email: string | null | undefined, fallback: string): string {
    if (!email) return fallback;
    const local = email.split('@')[0] || '';
    const cleaned = local.replace(/[._-]+/g, ' ').trim();

    if (!cleaned) return fallback;
    return cleaned
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function todayMeta(time: string, locale: string, todayLabel: string): string {
    const d = new Date();
    const day = d.toLocaleDateString(locale, { day: 'numeric' });
    const month = d.toLocaleDateString(locale, { month: 'long' });

    return `${todayLabel} · ${month.toUpperCase()} ${day} · ${time}`;
}

/**
 * All dimensions a task touches, with XP weight per dimension. Spreads each
 * tag's XP across `tag.dimension_weights` (a tag like "perfectionism" maps to
 * {start: 0.6, process: 0.4}), then adds the primary-dimension XP.
 *
 * Without this, contribution chips only ever show the *primary* dimension -
 * but a task that targets a multi-dimension tag should honestly show every
 * dimension it nudges.
 */
function expandedDimensionContributions(
    contribution: InterventionContribution | undefined,
): { id: string; pts: number }[] {
    if (!contribution) return [];

    return Object.entries(contribution.dimensionXP)
        .filter(([, p]) => p > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([id, pts]) => ({ id, pts: Math.round(pts) }));
}

function expandedPatternContributions(
    contribution: InterventionContribution | undefined,
): { id: string; pts: number }[] {
    if (!contribution) return [];

    return Object.entries(contribution.tagXP)
        .filter(([, pts]) => pts > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([id, pts]) => ({ id, pts: Math.round(pts) }));
}

/**
 * Compact time estimate for a task slot, e.g. "5 MIN", "30 MIN", "<1 MIN".
 * Reads `dosage.duration_minutes` first; falls back to parsing `time_label`,
 * then to "5 MIN" so every task gets *some* honest estimate.
 */
function compactTime(
    content: UnifiedContent | null,
    interventionId: string | undefined,
    unit: string,
): string {
    const fallback = `5 ${unit}`;

    if (!content || !interventionId) return fallback;
    const iv = content.interventions?.[interventionId];

    if (!iv) return fallback;
    const min = iv.dosage?.duration_minutes;

    if (typeof min === 'number' && min > 0) return `${min} ${unit}`;
    const label = iv.instructions?.time_label || '';
    const m = label.match(/(\d+)\s*(?:minute|min|хв|хвилин)\b/i);

    if (m) return `${m[1]} ${unit}`;
    if (/seconds?|секунд/i.test(label)) return `<1 ${unit}`;

    return fallback;
}

function todayHeader(locale: string): string {
    return new Date().toLocaleDateString(locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    });
}

const DashboardPage: React.FC = () => {
    const { user, userProfile, profileLoading, signOut } = useAuth();
    const { lang } = useLang();
    const t = useT();
    const navigate = useNavigate();
    const location = useLocation();

    const [scores, setScores] = useState<ScoringResult | null>(null);
    const [content, setContent] = useState<UnifiedContent | null>(null);
    const [dataLoading, setDataLoading] = useState(true);
    const [completed, setCompleted] = useState<Set<string>>(() => loadCompletedTasks(undefined));
    const [drill, setDrill] = useState<DrillState | null>(null);
    const [recalcNonce, setRecalcNonce] = useState(0);
    const [lastTakenAt, setLastTakenAt] = useState<Date | null>(null);
    // Tracks with a completed pass. Everything else renders as "not taken yet".
    const [measuredTracks, setMeasuredTracks] = useState<string[]>([]);
    const [patternFilter, setPatternFilter] = useState<'all' | 'shadow' | 'engine'>('all');
    const [taskSortDimension, setTaskSortDimension] = useState<DimensionId | null>(null);
    const [taskSortPattern, setTaskSortPattern] = useState<string | null>(null);
    const [taskSortPendulum, setTaskSortPendulum] = useState<string | null>(null);

    // Dashboard login / app open - one milestone event per authenticated session
    // (sessionStorage-guarded so navigating back to the dashboard doesn't re-log).
    useEffect(() => {
        if (!user?.id) return;
        try {
            if (sessionStorage.getItem('app_open_logged')) return;
            sessionStorage.setItem('app_open_logged', '1');
        } catch { /* ignore */ }
        trackEvent('app_open', { email: user.email || undefined });
    }, [user?.id, user?.email]);

    // ?recalc=1 → drop daily-focus lock + refetch content/scores, then strip the param.
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (params.get('recalc') !== '1') return;
        if (user?.id) clearRoadmapState(user.id);
        clearContentCache();
        setContent(null);
        setScores(null);
        setLastTakenAt(null);
        setDataLoading(true);
        setRecalcNonce((n) => n + 1);
        params.delete('recalc');
        const next = params.toString();

        navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
    }, [location.search, location.pathname, user?.id, navigate]);

    useEffect(() => {
        loadContent(lang)
            .then(setContent)
            .catch((e) => console.error('Failed to load content:', e));
    }, [recalcNonce, lang]);

    useEffect(() => {
        if (!user?.id || !content) {
            if (!user?.id) setDataLoading(false);
            return undefined;
        }
        let cancelled = false;

        const load = async () => {
            try {
                // Every pass is its own row, so a user who has taken three
                // directions has three rows. Read them all, not just the newest.
                const { data: rows } = await supabase
                    .from('results')
                    .select('answers, quest_name, created_at')
                    .eq('user_id', user.id)
                    .order('created_at', { ascending: false })
                    .limit(50);

                if (cancelled) return;
                if (!userProfile?.has_paid) return;
                if (!rows || rows.length === 0) return;

                if (rows[0].created_at) setLastTakenAt(new Date(rows[0].created_at));

                // Rows are newest-first, so the first row seen for a track is
                // the freshest pass — a retake supersedes the earlier attempt.
                const newestPerTrack = new Map<string, Record<string, any>>();

                rows.forEach((row) => {
                    const a = (row.answers || {}) as Record<string, any>;
                    const trackId: string = a._track || a.selected_track || a._recommended_track;

                    if (trackId && !newestPerTrack.has(trackId)) newestPerTrack.set(trackId, a);
                });
                if (newestPerTrack.size === 0) return;

                // Local-first fetch so Recalculate always picks up the latest
                // scoring rules from the repo (new patterns, weight tweaks).
                // Falls through to Supabase if no local quiz JSON matches.
                const shell =
                    (await loadQuizPages({
                        quizName: rows[0].quest_name || 'doyourstep-quiz',
                        lang,
                        aliases: { 'doyourstep-quiz': 'doyourstep_v3' },
                    })) || [];

                const submissions = await Promise.all(
                    Array.from(newestPerTrack).map(async ([trackId, answers]) => ({
                        track: trackId,
                        answers,
                        // The routing block is answered on every pass, so each
                        // track is scored against the shell plus its own file.
                        pages: [...shell, ...((await loadQuizPages({ quizName: `doyourstep_track_${trackId}`, lang })) || [])],
                    })),
                );

                if (cancelled) return;
                const tagTypes: Record<string, 'shadow' | 'engine'> = {};
                const tagDimension: Record<string, string> = {};

                Object.values(content.tags).forEach((tg) => {
                    tagTypes[tg.id] = tg.type;
                    tagDimension[tg.id] = tg.primary_dimension || Object.keys(tg.dimension_weights || {})[0] || '';
                });

                // Recompute from the answers rather than trusting the
                // `_scores_v3` snapshot taken at submit time — same reason
                // the fetch above is local-first.
                const merged = mergeTrackResults(submissions, tagTypes, tagDimension);

                setScores(merged.scores);
                setMeasuredTracks(merged.measuredTracks);
            } catch (err) {
                console.error('Error loading scores:', err);
            } finally {
                if (!cancelled) setDataLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [user, userProfile, content, recalcNonce]);

    useEffect(() => {
        if (user?.id) setCompleted(loadCompletedTasks(user.id));
    }, [user?.id]);

    const toggleTask = (taskId: string) => {
        setCompleted((prev) => {
            const next = new Set(prev);

            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            saveCompletedTasks(user?.id, next);
            return next;
        });
    };

    const composite = useMemo(() => (scores ? calculateDevelopmentScore(scores) : null), [scores]);

    const phases: RoadmapPhase[] = useMemo(() => {
        if (!content || !scores || !composite) return [];
        return buildPersonalRoadmap(scores, content, composite);
    }, [content, scores, composite]);

    const todayFocus: TodayFocus | null = useMemo(
        () => (composite && content ? pickTodayFocus(composite, phases, content, user?.id, completed) : null),
        [composite, content, phases, user?.id, completed],
    );

    const xp = useMemo(() => (content ? computeXP(completed, content) : null), [completed, content]);

    // Hooks below MUST be unconditional - they're called before any early return
    // so React's hook order stays stable across renders.
    const focusContributions: FocusContribution[] = useMemo(() => {
        if (!todayFocus?.contribution || !content) return [];

        const patterns = expandedPatternContributions(todayFocus.contribution)
            .slice(0, 2)
            .map(({ id, pts }) => {
                const pattern = content.tags?.[id];

                return {
                    dim: `${t('dashboard.tasks.contribution.pattern')} · ${pattern?.label || id}`,
                    pts,
                    color: pattern?.type === 'engine' ? '#22C55E' : '#EF4444',
                };
            });
        const dimensions = expandedDimensionContributions(todayFocus.contribution)
            .slice(0, 2)
            .map(({ id, pts }) => ({
                dim: content.dimensions?.[id]?.label || id,
                pts,
                color: DIMENSION_COLORS[id as DimensionId] || '#888',
            }));

        return [...patterns, ...dimensions];
    }, [todayFocus, content, t]);

    const howSteps = useMemo(() => {
        if (!todayFocus?.how) return [] as string[];
        return todayFocus.how
            .split(/\n+|(?<=\.)\s+(?=[A-Z])/)
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 6);
    }, [todayFocus]);

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    const handleRecalculate = () => {
        if (typeof window !== 'undefined' && content) {
            const ok = window.confirm(content.ui.nav.recalculate_confirm);

            if (!ok) return;
        }
        // This screen moved to /dashboard/legacy when the new app surface took
        // over /dashboard; recalculating must stay on the screen that offers it.
        navigate('/dashboard/legacy?recalc=1');
    };

    const ui = content?.ui;
    const locale = langToBcp47(lang);
    const durationUnit = t('dashboard.today.duration_unit');

    // Renders a single page-shell wrapper consistently for all states.
    const renderShell = (children: React.ReactNode) => (
        <div
            style={{
                minHeight: '100vh',
                background: colors.bg,
                color: colors.text,
                fontFamily: fonts.display,
                fontFeatureSettings: '"ss01","cv11","tnum"',
            }}
        >
            <div style={{ padding: '36px 56px 72px', maxWidth: 1440, margin: '0 auto' }}>
                <TopBar
                    dateLabel={todayHeader(locale)}
                    stageLabel={ui && xp && xp.totalXP > 0 ? `${ui.xp.level_prefix} ${xp.level}` : undefined}
                    pointsLabel={
                        xp && xp.totalXP > 0
                            ? `${formatXP(xp.totalXP, { compact: true })} ${ui?.xp.label || 'pts'}`
                            : undefined
                    }
                    onSignOut={handleSignOut}
                    onRecalculate={content ? handleRecalculate : undefined}
                    recalcLabel={ui?.nav.recalculate}
                    dashboardLabel={t('dashboard.topbar.dashboard')}
                    roadmapLabel={t('dashboard.topbar.roadmap')}
                    logoutLabel={t('dashboard.topbar.log_out')}
                />
                {children}
            </div>
        </div>
    );

    // Still resolving the profile - show the loading state, not the paywall.
    // Otherwise a logged-in paid user briefly flashes the paywall in the gap
    // between auth resolving and `userProfile` arriving.
    if (profileLoading) {
        return renderShell(
            <div
                style={{
                    padding: '120px 0',
                    textAlign: 'center',
                    color: colors.muted,
                    fontSize: 14,
                }}
            >
                {ui?.states.loading ?? 'Loading…'}
            </div>,
        );
    }

    if (!userProfile?.has_paid) {
        return renderShell(
            <div
                style={{
                    padding: '120px 0',
                    textAlign: 'center',
                    color: colors.muted,
                }}
            >
                <div style={{ fontSize: 48, marginBottom: 16 }}>{ui?.states.paywall_icon ?? '🎯'}</div>
                <h2
                    style={{
                        fontSize: 28,
                        color: colors.textPrimary,
                        fontWeight: 400,
                        margin: '0 0 12px',
                    }}
                >
                    {ui?.states.paywall_title ?? 'Unlock your dashboard'}
                </h2>
                <p style={{ fontSize: 16, margin: '0 0 24px' }}>
                    {ui?.states.paywall_body ?? 'Purchase to unlock your full diagnostic.'}
                </p>
                <Link
                    to="/pricing"
                    style={{
                        display: 'inline-block',
                        background: '#f4f2ee',
                        color: '#0a0a0a',
                        padding: '12px 24px',
                        borderRadius: 999,
                        textDecoration: 'none',
                        fontSize: 14,
                        fontWeight: 500,
                    }}
                >
                    {ui?.states.paywall_cta ?? 'Choose your plan →'}
                </Link>
            </div>,
        );
    }

    if (dataLoading || !content || !ui) {
        return renderShell(
            <div
                style={{
                    padding: '120px 0',
                    textAlign: 'center',
                    color: colors.muted,
                    fontSize: 14,
                }}
            >
                {ui?.states.loading ?? 'Loading…'}
            </div>,
        );
    }

    if (!scores || !composite) {
        return renderShell(
            <div style={{ padding: '120px 0', textAlign: 'center', color: colors.muted }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>{ui.states.no_results_icon}</div>
                <h2
                    style={{
                        fontSize: 28,
                        color: colors.textPrimary,
                        fontWeight: 400,
                        margin: '0 0 12px',
                    }}
                >
                    {ui.states.no_results_title}
                </h2>
                <p style={{ fontSize: 16, margin: '0 0 24px' }}>{ui.states.no_results_body}</p>
                <Link
                    to="/quiz/doyourstep-quiz"
                    style={{
                        display: 'inline-block',
                        background: '#f4f2ee',
                        color: '#0a0a0a',
                        padding: '12px 24px',
                        borderRadius: 999,
                        textDecoration: 'none',
                        fontSize: 14,
                        fontWeight: 500,
                    }}
                >
                    {ui.states.no_results_cta}
                </Link>
            </div>,
        );
    }

    const sc = scoreColor(composite.score);
    const userName = nameFromEmail(user?.email, ui.greetings.fallback_name);

    // ===== Dimensions data =====
    // A direction with no completed pass has no score at all — it renders locked
    // rather than at 0%, which would claim we measured it and found nothing.
    const measured = new Set(measuredTracks);
    const dimensionItems = DIMENSION_ORDER.map((dimId) => {
        const dim = scores.dimensions[dimId];
        const meta = content.dimensions?.[dimId];
        const dimXP = xp?.dimensionXP[dimId] ?? 0;

        return {
            id: dimId,
            label: meta?.label || dimId,
            subtitle: meta?.subtitle,
            percent: dim?.percent ?? 0,
            color: DIMENSION_COLORS[dimId] || meta?.color || '#888',
            weeklyPts: Math.round(dimXP),
            locked: !dim,
        };
    });

    // ===== Patterns data =====
    // Patterns follow their direction: measured ones sort by intensity, the rest
    // stay in the list as locked rows so the full model is still visible.
    const patternItems = Object.entries(content.tags)
        .map(([id, meta]) => {
            const score = scores.tags.find((tag) => tag.id === id);
            const dimId = meta.primary_dimension || Object.keys(meta.dimension_weights || {})[0] || '';

            return {
                id,
                label: meta.label || id,
                subtitle: meta.subtitle,
                pct: score?.percent ?? 0,
                pts: xp?.tagXP[id] ? Math.round(xp.tagXP[id]) : 0,
                type: meta.type,
                locked: !score || !measured.has(dimId),
                lockedDimension: dimId,
            };
        })
        .sort((a, b) => {
            if (a.locked !== b.locked) return a.locked ? 1 : -1;
            return b.pct - a.pct;
        });

    // ===== Pendulum data =====
    const sortedPendulums = [...scores.pendulums].sort((a, b) => b.amplitude - a.amplitude);

    // ===== Tasks data - map roadmap phases → editorial TaskList shape =====
    const months: MonthSummary[] = phases.map((phase) => {
        const doneCount = phase.tasks.filter((t) => completed.has(t.id)).length;
        let status: MonthSummary['status'];

        if (doneCount >= phase.tasks.length && phase.tasks.length > 0) status = 'done';
        else if (
            phase.index === 1 ||
            phases.slice(0, phase.index - 1).every((p) => p.tasks.every((t) => completed.has(t.id)))
        )
            status = 'active';
        else status = 'locked';
        // First non-fully-done month is active
        return {
            idx: phase.index,
            theme: phase.title,
            done: doneCount,
            total: phase.tasks.length,
            status,
        };
    });
    // Make sure exactly one month is "active" (the first not-done)
    const firstNotDone = months.find((m) => m.status !== 'done');

    months.forEach((m) => {
        if (m.status === 'done') return;
        m.status = m === firstNotDone ? 'active' : 'locked';
    });

    const activePhase = phases.find((p) => months.find((m) => m.idx === p.index)?.status === 'active');
    const patternContributionLabel = t('dashboard.tasks.contribution.pattern');
    const activeMonthTasks: TaskItem[] = activePhase
        ? activePhase.tasks.map((t, i) => {
              const isDone = completed.has(t.id);
              const isToday = !isDone && todayFocus?.taskId === t.id;
              const isQueued = !isDone && !isToday;
              const dimensionContributions = expandedDimensionContributions(t.contribution);
              const patternContributions = expandedPatternContributions(t.contribution);
              const orderedContributions = taskSortDimension
                  ? [...dimensionContributions].sort((a, b) => {
                        if (a.id === taskSortDimension) return -1;
                        if (b.id === taskSortDimension) return 1;
                        return b.pts - a.pts;
                    })
                  : dimensionContributions;
              const orderedPatterns = taskSortPattern
                  ? [...patternContributions].sort((a, b) => {
                        if (a.id === taskSortPattern) return -1;
                        if (b.id === taskSortPattern) return 1;
                        return b.pts - a.pts;
                    })
                  : patternContributions;
              const visiblePatterns = orderedPatterns.slice(0, 2).map(({ id, pts }) => {
                  const pattern = content.tags?.[id];

                  return {
                      dim: `${patternContributionLabel} · ${pattern?.label || id}`,
                      pts,
                      color: pattern?.type === 'engine' ? '#22C55E' : '#EF4444',
                  };
              });
              const contributions: { dim: string; pts: number; color: string }[] | undefined = t.contribution
                  ? [
                        ...(taskSortPendulum && (t.contribution.pendulumXP[taskSortPendulum] || 0) > 0
                            ? [
                                  {
                                      dim: content.pendulum_axes?.axes?.[taskSortPendulum]?.label || taskSortPendulum,
                                      pts: Math.round(t.contribution.pendulumXP[taskSortPendulum]),
                                      color: '#A78BFA',
                                  },
                              ]
                            : []),
                        ...visiblePatterns,
                        ...orderedContributions.slice(0, 2).map(({ id, pts }) => ({
                            dim: content.dimensions?.[id]?.label || id,
                            pts,
                            color: DIMENSION_COLORS[id as DimensionId] || '#888',
                        })),
                    ]
                  : undefined;
              const taskSteps = t.how
                  ? t.how
                        .split(/\n+|(?<=\.)\s+(?=[A-Z])/)
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .slice(0, 6)
                  : undefined;
              const tagId = (t.dimensionId as string) || 'task';

              return {
                  n: i + 1,
                  title: t.task,
                  tag: content.dimensions?.[tagId]?.label || tagId,
                  pts: Math.round(t.contribution?.totalXP ?? 0),
                  time: compactTime(content, t.interventionId, durationUnit),
                  timeFull: t.time || undefined,
                  status: ((): TaskItem['status'] => {
                      if (isDone) return 'done';
                      if (isToday) return 'today';
                      return 'queued';
                  })(),
                  description: taskLeadPreview(t),
                  howSteps: taskSteps,
                  why: t.why,
                  contributions,
                  dimensionPoints: t.contribution?.dimensionXP || {},
                  patternPoints: t.contribution?.tagXP || {},
                  pendulumPoints: t.contribution?.pendulumXP || {},
              } as TaskItem;
          })
        : [];
    let orderedActiveMonthTasks = activeMonthTasks;

    if (taskSortPendulum) {
        orderedActiveMonthTasks = [...activeMonthTasks].sort((a, b) => {
            const delta =
                (b.pendulumPoints?.[taskSortPendulum] || 0) -
                (a.pendulumPoints?.[taskSortPendulum] || 0);

            return delta || a.n - b.n;
        });
    } else if (taskSortPattern) {
        orderedActiveMonthTasks = [...activeMonthTasks].sort((a, b) => {
            const delta =
                (b.patternPoints?.[taskSortPattern] || 0) -
                (a.patternPoints?.[taskSortPattern] || 0);

            return delta || a.n - b.n;
        });
    } else if (taskSortDimension) {
        orderedActiveMonthTasks = [...activeMonthTasks].sort((a, b) => {
            const delta =
                (b.dimensionPoints?.[taskSortDimension] || 0) -
                (a.dimensionPoints?.[taskSortDimension] || 0);

            return delta || a.n - b.n;
        });
    }
    const selectedTaskDimension = taskSortDimension ? content.dimensions?.[taskSortDimension] : null;
    const selectedTaskPattern = taskSortPattern ? content.tags?.[taskSortPattern] : null;
    const selectedTaskPendulum = taskSortPendulum
        ? content.pendulum_axes?.axes?.[taskSortPendulum]
        : null;
    let taskSortHint = t('dashboard.tasks.sort.hint_recommended');

    if (selectedTaskPendulum) {
        taskSortHint = t('dashboard.tasks.sort.hint_pendulum', { pendulum: selectedTaskPendulum.label });
    } else if (selectedTaskPattern) {
        taskSortHint = t('dashboard.tasks.sort.hint_pattern', { pattern: selectedTaskPattern.label });
    } else if (selectedTaskDimension) {
        taskSortHint = t('dashboard.tasks.sort.hint_dimension', { dimension: selectedTaskDimension.label });
    }

    const handleDimensionPractice = (dimensionId: DimensionId) => {
        setTaskSortPattern(null);
        setTaskSortPendulum(null);
        setTaskSortDimension(dimensionId);
        setDrill(null);
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                document.getElementById('tasks-roadmap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    };

    const handlePatternPractice = (patternId: string) => {
        setTaskSortDimension(null);
        setTaskSortPendulum(null);
        setTaskSortPattern(patternId);
        setDrill(null);
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                document.getElementById('tasks-roadmap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    };

    const handlePendulumPractice = (pendulumId: string) => {
        setTaskSortDimension(null);
        setTaskSortPattern(null);
        setTaskSortPendulum(pendulumId);
        setDrill(null);
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                document.getElementById('tasks-roadmap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    };

    // ===== Drawer drill-down content =====
    const drillContent = (() => {
        if (!drill) return null;

        if (drill.kind === 'dimension') {
            const meta = content.dimensions?.[drill.id];

            if (!meta) return null;
            const dim = scores.dimensions[drill.id];
            const level = dimensionLevel(dim?.percent ?? 0);
            const levelDesc = meta.level_descriptions?.[level.key];
            const accent = meta.color || DIMENSION_COLORS[drill.id as DimensionId] || '#3B82F6';

            return {
                title: meta.label,
                eyebrow: `DIMENSION · ${dim?.percent ?? 0}% · ${level.label.toUpperCase()}`,
                color: accent,
                body: (
                    <>
                        <DetailHero question={meta.question} description={meta.description} />

                        {meta.what_we_measure && meta.what_we_measure.length > 0 && (
                            <DetailSection label={t('dashboard.detail.what_we_measure')}>
                                <DetailMeasureList items={meta.what_we_measure} accentColor={accent} />
                            </DetailSection>
                        )}

                        {levelDesc && (
                            <DetailSection label={t('dashboard.detail.where_you_stand')}>
                                <DetailInsightCard
                                    body={levelDesc.description}
                                    cite={levelDesc.priority_message}
                                    accentColor={accent}
                                    italic
                                />
                            </DetailSection>
                        )}

                        {meta.common_problems && meta.common_problems.length > 0 && (
                            <DetailSection label={t('dashboard.detail.common_traps')}>
                                <DetailMeasureList items={meta.common_problems} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.theory && meta.theory.length > 0 && (
                            <DetailSection label={t('dashboard.detail.how_it_works')}>
                                {meta.theory.map((paragraph: string) => (
                                    <p
                                        key={paragraph}
                                        style={{
                                            margin: '0 0 14px',
                                            color: '#d8d6d0',
                                            fontSize: 14,
                                            lineHeight: 1.7,
                                        }}
                                    >
                                        {paragraph}
                                    </p>
                                ))}
                            </DetailSection>
                        )}

                        {meta.aha_insights && meta.aha_insights.length > 0 && (
                            <DetailSection label={t('dashboard.detail.research_that_matters')}>
                                {meta.aha_insights.slice(0, 3).map((ins: any) => (
                                    <DetailInsightCard
                                        key={ins.insight}
                                        body={ins.insight}
                                        cite={ins.source}
                                        sourceUrl={ins.source_url}
                                        accentColor={accent}
                                    />
                                ))}
                            </DetailSection>
                        )}

                        {meta.how_it_helps && meta.how_it_helps.length > 0 && (
                            <DetailSection label={t('dashboard.detail.how_it_helps')}>
                                <DetailMeasureList items={meta.how_it_helps} accentColor={accent} />
                            </DetailSection>
                        )}

                        <div
                            style={{
                                marginTop: 28,
                                padding: '18px 20px',
                                borderRadius: 12,
                                background: `${accent}0D`,
                                border: `1px solid ${accent}33`,
                            }}
                        >
                            <p
                                style={{
                                    margin: '0 0 14px',
                                    color: colors.muted,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                }}
                            >
                                {t('dashboard.detail.practice_cta_body', { dimension: meta.label })}
                            </p>
                            <button
                                type="button"
                                onClick={() => handleDimensionPractice(drill.id as DimensionId)}
                                style={{
                                    border: 'none',
                                    borderRadius: 999,
                                    padding: '10px 17px',
                                    background: accent,
                                    color: '#090909',
                                    fontFamily: 'inherit',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {t('dashboard.detail.practice_cta')}
                            </button>
                        </div>
                    </>
                ),
            };
        }

        if (drill.kind === 'pattern') {
            const meta = content.tags?.[drill.id];

            if (!meta) return null;
            const tag = scores.tags.find((t) => t.id === drill.id);
            const accent = tag?.type === 'engine' ? '#22C55E' : '#EF4444';
            let scoreLabel = `${tag?.percent ?? 0}% ${t('dashboard.eyebrow.intensity')}`;

            if ((tag?.percent ?? 0) === 0) {
                scoreLabel = tag?.type === 'engine'
                    ? t('dashboard.patterns.zero.resource')
                    : t('dashboard.patterns.zero.blocker');
            }

            return {
                title: meta.label || drill.id,
                eyebrow: `${t('dashboard.eyebrow.pattern')} · ${scoreLabel} · ${tag?.type === 'engine' ? t('dashboard.eyebrow.resource') : t('dashboard.eyebrow.blocker')}`,
                color: accent,
                body: (
                    <>
                        <DetailHero description={meta.description} />

                        {meta.score_note && (
                            <DetailSection label={t('dashboard.detail.how_to_read_score')}>
                                <DetailInsightCard body={meta.score_note} accentColor={accent} italic />
                            </DetailSection>
                        )}

                        {meta.real_life_examples && meta.real_life_examples.length > 0 && (
                            <DetailSection label={t('dashboard.detail.real_life')}>
                                <DetailMeasureList items={meta.real_life_examples} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.mechanism && (
                            <DetailSection label={t('dashboard.detail.mechanism')}>
                                <DetailInsightCard body={meta.mechanism} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.aha_insight && (
                            <DetailSection label={t('dashboard.detail.the_insight')}>
                                <DetailInsightCard body={meta.aha_insight} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.research?.key_study && (
                            <DetailSection label={t('dashboard.detail.research_backing')}>
                                <DetailInsightCard
                                    body={meta.research.key_study}
                                    cite={meta.research.reference}
                                    sourceUrl={meta.research.source_url}
                                    accentColor={accent}
                                />
                            </DetailSection>
                        )}

                        {meta.how_it_helps && meta.how_it_helps.length > 0 && (
                            <DetailSection label={t('dashboard.detail.how_it_helps')}>
                                <DetailMeasureList items={meta.how_it_helps} accentColor={accent} />
                            </DetailSection>
                        )}

                        <div
                            style={{
                                marginTop: 28,
                                padding: '18px 20px',
                                borderRadius: 12,
                                background: `${accent}0D`,
                                border: `1px solid ${accent}33`,
                            }}
                        >
                            <p
                                style={{
                                    margin: '0 0 14px',
                                    color: colors.muted,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                }}
                            >
                                {t('dashboard.detail.pattern_practice_cta_body', { pattern: meta.label })}
                            </p>
                            <button
                                type="button"
                                onClick={() => handlePatternPractice(drill.id)}
                                style={{
                                    border: 'none',
                                    borderRadius: 999,
                                    padding: '10px 17px',
                                    background: accent,
                                    color: '#090909',
                                    fontFamily: 'inherit',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {t('dashboard.detail.pattern_practice_cta')}
                            </button>
                        </div>
                    </>
                ),
            };
        }

        if (drill.kind === 'pendulum') {
            const meta = content.pendulum_axes?.axes?.[drill.id];

            if (!meta) return null;
            const p = scores.pendulums.find((x) => x.id === drill.id);
            const ampPct = p ? Math.round(p.amplitude * 100) : 0;
            const band = amplitudeBand(ampPct);
            const accent = pendulumZoneColor(ampPct);
            const leftPole = meta.poles?.[meta.left_pole];
            const rightPole = meta.poles?.[meta.right_pole];

            return {
                title: meta.label || drill.id,
                eyebrow: `${t('dashboard.eyebrow.pendulum')} · ${t('dashboard.eyebrow.amplitude')} ${ampPct}% · ${band.label.toUpperCase()}`,
                color: accent,
                body: (
                    <>
                        <DetailHero question={meta.question} description={meta.description} />

                        {meta.score_note && (
                            <DetailSection label={t('dashboard.detail.how_to_read_amplitude')}>
                                <DetailInsightCard body={meta.score_note} accentColor={accent} italic />
                            </DetailSection>
                        )}

                        {leftPole && rightPole && (
                            <DetailSection label={t('dashboard.detail.two_edges')}>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                        gap: 12,
                                    }}
                                >
                                    {[leftPole, rightPole].map((pole) => (
                                        <div
                                            key={pole.label}
                                            style={{
                                                minHeight: 150,
                                                padding: '16px 18px',
                                                borderRadius: 10,
                                                background: 'rgba(255,255,255,0.025)',
                                                border: `1px solid ${accent}2E`,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginBottom: 10,
                                                    color: colors.textPrimary,
                                                    fontSize: 15,
                                                    fontWeight: 600,
                                                    lineHeight: 1.35,
                                                }}
                                            >
                                                {pole.label}
                                            </div>
                                            <p
                                                style={{
                                                    margin: 0,
                                                    color: colors.muted,
                                                    fontSize: 13,
                                                    lineHeight: 1.65,
                                                }}
                                            >
                                                {pole.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </DetailSection>
                        )}

                        {meta.mechanism && (
                            <DetailSection label={t('dashboard.detail.how_it_works')}>
                                <DetailInsightCard body={meta.mechanism} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.real_life_examples && meta.real_life_examples.length > 0 && (
                            <DetailSection label={t('dashboard.detail.real_life')}>
                                <DetailMeasureList items={meta.real_life_examples} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.aha_insight && (
                            <DetailSection label={t('dashboard.detail.the_insight')}>
                                <DetailInsightCard body={meta.aha_insight} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.research?.key_study && (
                            <DetailSection label={t('dashboard.detail.research_backing')}>
                                <DetailInsightCard
                                    body={meta.research.key_study}
                                    cite={meta.research.reference}
                                    sourceUrl={meta.research.source_url}
                                    accentColor={accent}
                                />
                            </DetailSection>
                        )}

                        {meta.flexible_middle && (
                            <DetailSection label={t('dashboard.detail.flexible_center')}>
                                <DetailInsightCard body={meta.flexible_middle} accentColor="#22C55E" italic />
                            </DetailSection>
                        )}

                        {meta.how_it_helps && meta.how_it_helps.length > 0 && (
                            <DetailSection label={t('dashboard.detail.how_it_helps')}>
                                <DetailMeasureList items={meta.how_it_helps} accentColor={accent} />
                            </DetailSection>
                        )}

                        {meta.regulation_strategies && meta.regulation_strategies.length > 0 && (
                            <DetailSection label={t('dashboard.detail.regulation')}>
                                <DetailMeasureList
                                    items={meta.regulation_strategies.slice(0, 4)}
                                    accentColor={accent}
                                />
                            </DetailSection>
                        )}

                        <div
                            style={{
                                marginTop: 28,
                                padding: '18px 20px',
                                borderRadius: 12,
                                background: `${accent}0D`,
                                border: `1px solid ${accent}33`,
                            }}
                        >
                            <p
                                style={{
                                    margin: '0 0 14px',
                                    color: colors.muted,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                }}
                            >
                                {t('dashboard.detail.pendulum_practice_cta_body', { pendulum: meta.label })}
                            </p>
                            <button
                                type="button"
                                onClick={() => handlePendulumPractice(drill.id)}
                                style={{
                                    border: 'none',
                                    borderRadius: 999,
                                    padding: '10px 17px',
                                    background: accent,
                                    color: '#090909',
                                    fontFamily: 'inherit',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {t('dashboard.detail.pendulum_practice_cta')}
                            </button>
                        </div>
                    </>
                ),
            };
        }
        return null;
    })();

    return (
        <div
            className="dash-edit"
            style={{
                minHeight: '100vh',
                background: colors.bg,
                color: colors.text,
                fontFamily: fonts.display,
                fontFeatureSettings: '"ss01","cv11","tnum"',
            }}
        >
            <div className="dash-edit__shell" style={{ padding: '36px 56px 0', maxWidth: 1440, margin: '0 auto' }}>
                <TopBar
                    dateLabel={todayHeader(locale)}
                    stageLabel={xp && xp.totalXP > 0 ? `${ui.xp.level_prefix} ${xp.level}` : undefined}
                    pointsLabel={
                        xp && xp.totalXP > 0 ? `${formatXP(xp.totalXP, { compact: true })} ${ui.xp.label}` : undefined
                    }
                    onSignOut={handleSignOut}
                    onRecalculate={handleRecalculate}
                    recalcLabel={ui.nav.recalculate}
                />

                {/* DATA-QUALITY NOTE - non-blocking; shown if validity checks flagged the response */}
                {scores.validity && scores.validity.flags.length > 0 && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: '12px 16px',
                            borderRadius: 12,
                            background: 'rgba(245, 158, 11, 0.10)',
                            border: '1px solid rgba(245, 158, 11, 0.35)',
                            color: '#F59E0B',
                            fontSize: 13,
                            lineHeight: 1.5,
                        }}
                    >
                        {locale?.startsWith('uk')
                            ? 'Деякі відповіді виглядають поспішними або суперечливими - профіль показано, але для точнішого результату варто пройти тест ще раз уважно.'
                            : 'Some answers look rushed or inconsistent - your profile is shown, but a careful retake will make it more accurate.'}
                    </div>
                )}

                {/* HERO */}
                <div
                    className="dash-edit__hero"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1.15fr',
                        gap: 56,
                        alignItems: 'stretch',
                        padding: '36px 0 56px',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <div>
                            <div
                                style={{
                                    fontSize: 11,
                                    letterSpacing: '0.22em',
                                    color: colors.muted,
                                    marginBottom: 12,
                                }}
                            >
                                {ui.greetings[
                                    (() => {
                                        const h = new Date().getHours();

                                        if (h < 5) return 'night';
                                        if (h < 12) return 'morning';
                                        if (h < 17) return 'afternoon';
                                        if (h < 22) return 'evening';
                                        return 'night';
                                    })() as 'morning' | 'afternoon' | 'evening' | 'night'
                                ]
                                    .toString()
                                    .toUpperCase()}
                            </div>
                            <h1
                                className="dash-edit__greeting"
                                style={{
                                    fontSize: 44,
                                    fontWeight: 300,
                                    letterSpacing: '-0.03em',
                                    margin: 0,
                                    lineHeight: 1.05,
                                }}
                            >
                                <span style={{ color: colors.textPrimary }}>{userName}</span>
                                <span style={{ color: colors.muted }}>,</span>
                                <br />
                                <span style={{ color: colors.muted }}>your week looks </span>
                                <span style={{ color: sc.hex }}>{sc.name.toLowerCase()}.</span>
                            </h1>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            className="dash-edit__orb-wrap"
                        >
                            <HeroOrb
                                score={composite.score}
                                color={sc}
                                eyebrowLabel={t('dashboard.hero.development_score')}
                            />
                        </div>
                    </div>

                    <div className="dash-today-focus">
                    {todayFocus ? (
                        <FocusCard
                            title={todayFocus.task}
                            tag={
                                todayFocus.dimensionId ? content.dimensions?.[todayFocus.dimensionId]?.label : undefined
                            }
                            eyebrowMeta={todayMeta(
                                compactTime(content, ivIdFromTaskId(todayFocus.taskId), durationUnit),
                                locale,
                                t('dashboard.today.prefix'),
                            )}
                            howLabel={t('dashboard.focus.how_label')}
                            whyLabel={t('dashboard.focus.why_label')}
                            contributesLabel={t('dashboard.focus.contributes_label')}
                            showMoreLabel={t('dashboard.focus.show_more')}
                            showLessLabel={t('dashboard.focus.show_less')}
                            ptsUnit={t('dashboard.focus.pts_unit')}
                            description={todayFocus.summary || todayFocus.description}
                            howSteps={howSteps}
                            why={todayFocus.why}
                            contributions={focusContributions}
                            isCompleted={completed.has(todayFocus.taskId)}
                            onMarkDone={() => toggleTask(todayFocus.taskId)}
                            primaryLabel={ui.today_focus.cta_pending}
                            primaryDoneLabel={ui.today_focus.cta_done}
                        />
                    ) : (
                        <div
                            style={{
                                borderRadius: 20,
                                background: colors.cardBg,
                                border: '1px solid rgba(232,230,224,0.08)',
                                padding: '48px 36px',
                                color: colors.muted,
                                textAlign: 'center',
                                fontSize: 14,
                            }}
                        >
                            No focus task yet. Complete the quiz to receive your daily focus.
                        </div>
                    )}
                    </div>
                </div>

                {/* DIMENSIONS */}
                <section style={{ paddingTop: 36 }}>
                    <div
                        className="dash-edit__section-head"
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            marginBottom: 24,
                        }}
                    >
                        <div>
                            <h2
                                style={{
                                    fontSize: 28,
                                    fontWeight: 400,
                                    letterSpacing: '-0.02em',
                                    margin: 0,
                                }}
                            >
                                {ui.sections.dimensions.title}
                            </h2>
                            <p
                                style={{
                                    fontSize: 14,
                                    color: colors.muted,
                                    margin: '6px 0 0',
                                    maxWidth: 720,
                                    lineHeight: 1.5,
                                }}
                            >
                                {ui.sections.dimensions.subtitle}
                            </p>
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                color: colors.muted,
                                fontFamily: fonts.mono,
                            }}
                        >
                            {ui.sections.dimensions.meta_label} {composite.dimensionMean}%
                        </div>
                    </div>
                    <div
                        className="dash-edit__dim-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: 14,
                        }}
                    >
                        {dimensionItems.map((d) => (
                            <DimensionCard
                                key={d.id}
                                label={d.label}
                                subtitle={d.subtitle}
                                percent={d.percent}
                                color={d.color}
                                weeklyPts={d.weeklyPts}
                                locked={d.locked}
                                lockedLabel={t('dashboard.dimensions.not_taken')}
                                onClick={
                                    d.locked
                                        ? () => navigate(extendedQuizPath(d.id))
                                        : () => setDrill({ kind: 'dimension', id: d.id })
                                }
                            />
                        ))}
                    </div>
                </section>

                {/* PATTERNS */}
                <section style={{ paddingTop: 56 }}>
                    <div
                        className="dash-edit__section-head"
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            marginBottom: 20,
                        }}
                    >
                        <div>
                            <h2
                                style={{
                                    fontSize: 28,
                                    fontWeight: 400,
                                    letterSpacing: '-0.02em',
                                    margin: 0,
                                }}
                            >
                                {ui.sections.patterns.title}
                            </h2>
                            <p
                                style={{
                                    fontSize: 14,
                                    color: colors.muted,
                                    margin: '6px 0 0',
                                    maxWidth: 560,
                                    lineHeight: 1.5,
                                }}
                            >
                                {ui.sections.patterns.subtitle}
                            </p>
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                color: colors.muted,
                                fontFamily: fonts.mono,
                                letterSpacing: '0.1em',
                            }}
                        >
                            {patternItems.length} {ui.sections.patterns.meta_label}
                        </div>
                    </div>
                    {(() => {
                        const filterTabs: { key: 'all' | 'shadow' | 'engine'; label: string; count: number }[] = [
                            { key: 'all', label: t('dashboard.patterns.filter.all'), count: patternItems.length },
                            {
                                key: 'shadow',
                                label: t('dashboard.patterns.filter.blockers'),
                                count: patternItems.filter((p) => p.type === 'shadow').length,
                            },
                            {
                                key: 'engine',
                                label: t('dashboard.patterns.filter.resources'),
                                count: patternItems.filter((p) => p.type === 'engine').length,
                            },
                        ];
                        const filtered =
                            patternFilter === 'all'
                                ? patternItems
                                : patternItems.filter((p) => p.type === patternFilter);

                        return (
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        marginBottom: 16,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {filterTabs.map((tab) => {
                                        const active = patternFilter === tab.key;

                                        return (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                onClick={() => setPatternFilter(tab.key)}
                                                style={{
                                                    background: active ? colors.textPrimary : 'transparent',
                                                    color: active ? colors.bg : colors.muted,
                                                    border: active
                                                        ? `1px solid ${colors.textPrimary}`
                                                        : '1px solid rgba(232,230,224,0.14)',
                                                    padding: '6px 14px',
                                                    borderRadius: 999,
                                                    fontSize: 11,
                                                    fontFamily: fonts.mono,
                                                    letterSpacing: '0.1em',
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    textTransform: 'uppercase',
                                                    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                                                }}
                                            >
                                                {tab.label} <span style={{ opacity: 0.6 }}>({tab.count})</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <PatternsList
                                    patterns={filtered}
                                    onSelect={(id) => {
                                        const row = patternItems.find((p) => p.id === id);

                                        // A locked pattern has no detail to show —
                                        // send the user to measure it instead.
                                        if (row?.locked) navigate(extendedQuizPath(row.lockedDimension));
                                        else setDrill({ kind: 'pattern', id });
                                    }}
                                    zeroBlockerLabel={t('dashboard.patterns.zero.blocker')}
                                    zeroResourceLabel={t('dashboard.patterns.zero.resource')}
                                    lockedLabel={t('dashboard.patterns.not_taken')}
                                />
                            </>
                        );
                    })()}
                </section>

                {/*
                    PENDULUMS - commented out with the switch to the Notion model v2.
                    That model scores the 8 tracks and 53 patterns directly from the
                    quiz answers; it has no mirrored oscillation axes, so
                    taxonomy.pendulum_axes is empty and this section would render a
                    zero-item grid. Kept here rather than deleted so it can come back
                    if the model reintroduces axes.
                */}
                {
                    /*
                <section style={{ paddingTop: 56 }}>
                    <div
                        className="dash-edit__section-head"
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            marginBottom: 28,
                        }}
                    >
                        <div>
                            <h2
                                style={{
                                    fontSize: 28,
                                    fontWeight: 400,
                                    letterSpacing: '-0.02em',
                                    margin: 0,
                                }}
                            >
                                {ui.sections.pendulums.title}
                            </h2>
                            <p
                                style={{
                                    fontSize: 14,
                                    color: colors.muted,
                                    margin: '6px 0 0',
                                    maxWidth: 620,
                                    lineHeight: 1.5,
                                }}
                            >
                                {ui.sections.pendulums.subtitle}
                            </p>
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                color: colors.muted,
                                fontFamily: fonts.mono,
                                letterSpacing: '0.1em',
                            }}
                        >
                            {sortedPendulums.length} {ui.sections.pendulums.meta_label}
                        </div>
                    </div>
                    <div
                        className="dash-edit__pend-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.min(sortedPendulums.length || 1, 3)}, 1fr)`,
                            rowGap: 64,
                            columnGap: 32,
                        }}
                    >
                        {sortedPendulums.map((p) => {
                            const meta = content.pendulum_axes?.axes?.[p.id];
                            const pendulumXP = xp?.pendulumXP[p.id] ?? 0;
                            const leftLabel = meta?.poles?.[meta.left_pole]?.label || p.leftPole.id;
                            const rightLabel = meta?.poles?.[meta.right_pole]?.label || p.rightPole.id;

                            return (
                                <PendulumGauge
                                    key={p.id}
                                    id={p.id}
                                    amplitude={p.amplitude}
                                    position={p.position}
                                    leftLabel={leftLabel}
                                    rightLabel={rightLabel}
                                    axisLabel={meta?.label || `${leftLabel} ↔ ${rightLabel}`}
                                    axisSubtitle={meta?.subtitle || meta?.description || p.name}
                                    weeklyPts={Math.round(pendulumXP)}
                                    centered={p.centered}
                                    onClick={() => setDrill({ kind: 'pendulum', id: p.id })}
                                />
                            );
                        })}
                    </div>
                </section>
                    */
                }
            </div>

            {/* TASKS - Variant C - has its own padding so we exit the centered container */}
            <section id="tasks-roadmap" style={{ background: colors.bg, maxWidth: 1440, margin: '0 auto' }}>
                {months.length > 0 && activeMonthTasks.length > 0 ? (
                    <TaskList
                        months={months}
                        activeMonthTasks={orderedActiveMonthTasks}
                        score={sc}
                        headerTitle={ui.sections.roadmap.title}
                        headerSubtitle={ui.sections.roadmap.subtitle}
                        sortOptions={DIMENSION_ORDER.map((id) => ({
                            id,
                            label: content.dimensions?.[id]?.label || id,
                            color: DIMENSION_COLORS[id],
                        }))}
                        sortDimension={taskSortDimension}
                        onSortDimensionChange={(id) => {
                            setTaskSortPattern(null);
                            setTaskSortPendulum(null);
                            setTaskSortDimension(id as DimensionId | null);
                        }}
                        patternSortOptions={patternItems.map((pattern) => ({
                            id: pattern.id,
                            label: `${pattern.type === 'shadow' ? t('dashboard.eyebrow.blocker') : t('dashboard.eyebrow.resource')} · ${pattern.label}`,
                            color: pattern.type === 'engine' ? '#22C55E' : '#EF4444',
                        }))}
                        sortPattern={taskSortPattern}
                        onSortPatternChange={(id) => {
                            setTaskSortDimension(null);
                            setTaskSortPendulum(null);
                            setTaskSortPattern(id);
                        }}
                        pendulumSortOptions={sortedPendulums.map((pendulum) => ({
                            id: pendulum.id,
                            label: content.pendulum_axes?.axes?.[pendulum.id]?.label || pendulum.id,
                            color: '#A78BFA',
                        }))}
                        sortPendulum={taskSortPendulum}
                        onSortPendulumChange={(id) => {
                            setTaskSortDimension(null);
                            setTaskSortPattern(null);
                            setTaskSortPendulum(id);
                        }}
                        sortLabel={t('dashboard.tasks.sort.label')}
                        recommendedLabel={t('dashboard.tasks.sort.recommended')}
                        patternSortLabel={t('dashboard.tasks.sort.pattern')}
                        pendulumSortLabel={t('dashboard.tasks.sort.pendulum')}
                        sortHint={taskSortHint}
                        onMarkDone={(taskN) => {
                            const t = activePhase?.tasks[taskN - 1];

                            if (t) toggleTask(t.id);
                        }}
                    />
                ) : null}
            </section>

            {/* RETAKE ASSESSMENT */}
            <RetakeSection
                lastTakenAt={lastTakenAt}
                score={sc}
                onRetake={() => navigate('/quiz/doyourstep-quiz')}
            />

            {/* DRAWER */}
            {drill && drillContent && (
                <DrillDrawer
                    open
                    onClose={() => setDrill(null)}
                    title={drillContent.title}
                    eyebrow={drillContent.eyebrow}
                    color={drillContent.color}
                >
                    {drillContent.body}
                </DrillDrawer>
            )}
        </div>
    );
};

export default DashboardPage;
